import { useAuth } from "@dl/web-core-js/auth";
import { Badge, Button, cn, Segmented, Switch, toast } from "@dl/web-core-js/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { CheckCircle2, PlayCircle } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { refusalMessage } from "../../../api/manufacturing";
import { PurchaseOrderClient } from "../../../api/purchasing";
import { ReplenishmentClient, replenishmentKeys } from "../../../api/replenishment";
import { SupplierLink } from "../../../components/SupplierLink";
import { formatCad } from "./shared";

/**
 * Orders to chase: issued with a line past its date, or held back from the
 * supplier.
 *
 * <p>SIGM never closed an order, so hundreds of imported ones sit "issued"
 * years after the last delivery. Orders whose every open line is past the
 * stale-order days are stale: planning and the cash projection already ignore
 * them, so they are hidden here by default — they want closing, not chasing —
 * and one switch shows them.</p>
 */

type Show = "all" | "late" | "held";

export function LateTab() {
  const { t } = useTranslation();
  const { http, canWrite } = useAuth();
  const queryClient = useQueryClient();
  const client = useMemo(() => new ReplenishmentClient(http), [http]);
  const [show, setShow] = useState<Show>("all");
  const [history, setHistory] = useState(false);

  const late = useQuery({ queryKey: replenishmentKeys.late, queryFn: () => client.late() });

  const release = useMutation({
    mutationFn: (id: number) => new PurchaseOrderClient(http).release(id),
    onSuccess: (order) => {
      toast.success(t("replenishment.late.released", "#{{number}} released: the supplier ships, lead time counts from today.", { number: order.number }));
      void queryClient.invalidateQueries({ queryKey: replenishmentKeys.all });
      void queryClient.invalidateQueries({ queryKey: ["purchasing", "purchase-order"] });
    },
    onError: (failure) => toast.error(refusalMessage(failure, t("error.generic", "Something went wrong."))),
  });

  const all = late.data ?? [];
  const stale = all.filter((order) => order.is_stale).length;
  const rows = all.filter((order) => {
    if (!history && order.is_stale) return false;
    if (show === "late") return order.days_late > 0;
    if (show === "held") return order.is_on_hold;
    return true;
  });

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex flex-wrap items-center gap-3 border-b border-line-soft bg-surface px-4 py-2 lg:px-6">
        <Segmented<Show>
          value={show}
          onChange={setShow}
          options={[
            { value: "all", label: t("replenishment.late.all", "All") },
            { value: "late", label: t("replenishment.late.late", "Late") },
            { value: "held", label: t("replenishment.late.held", "On hold") },
          ]}
        />
        {stale > 0 && (
          <Switch
            checked={history}
            onChange={setHistory}
            label={t("replenishment.late.history", "Show {{count}} stale orders to close", { count: stale })}
            className="text-[12px] text-ink-muted"
          />
        )}
      </div>
      <div className="min-h-0 flex-1 overflow-auto bg-ground px-2 py-3 sm:px-4 lg:px-6">
        {late.isError ? (
          <p className="m-2 rounded-md bg-risk-soft px-3 py-2 text-[13px] text-risk">
            {t("replenishment.late.error", "The late orders could not be loaded.")}
          </p>
        ) : late.isLoading ? (
          <p className="p-6 text-[13px] text-ink-muted">{t("table.loading", "Loading…")}</p>
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center gap-2 px-6 py-16 text-center">
            <CheckCircle2 className="size-8 text-ok" />
            <h3 className="text-[15px] font-semibold text-ink">{t("replenishment.late.none", "Nothing to chase")}</h3>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-line bg-surface shadow-card">
            <table className="w-full min-w-[44rem] text-[13px]">
              <thead className="border-b border-line bg-surface-sunk text-left text-[11px] uppercase tracking-wider text-ink-faint">
                <tr>
                  <th className="px-4 py-2 font-medium">{t("column.po.number", "Order")}</th>
                  <th className="px-3 py-2 font-medium">{t("column.po.supplier", "Supplier")}</th>
                  <th className="px-3 py-2 font-medium">{t("replenishment.late.why", "Why")}</th>
                  <th className="px-3 py-2 text-right font-medium">{t("replenishment.late.due", "Due")}</th>
                  <th className="px-3 py-2 text-right font-medium">{t("replenishment.late.outstanding", "Still expected")}</th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody>
                {rows.map((order) => (
                  <tr key={order.purchase_order_id} className="border-b border-line-soft last:border-b-0">
                    <td className="px-4 py-2.5">
                      <Link
                        to="/purchasing/purchase-orders/$orderId"
                        params={{ orderId: String(order.purchase_order_id) }}
                        className="font-mono font-semibold text-ink hover:text-accent"
                      >
                        #{order.number}
                      </Link>
                      {order.is_from_sigm && <span className="ml-2 text-[11px] text-ink-faint">SIGM</span>}
                    </td>
                    <td className="max-w-56 truncate px-3 py-2.5 text-ink">
                      <SupplierLink id={order.supplier_id}>{order.supplier_name}</SupplierLink>
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex flex-wrap gap-1.5">
                        {order.days_late > 0 && (
                          <Badge variant={order.days_late > 14 ? "error" : "warning"} size="sm">
                            {order.late_line_count === 1
                              ? t("replenishment.late.days-one-line", "{{count}} d late · 1 line", { count: order.days_late })
                              : t("replenishment.late.days", "{{count}} d late · {{lines}} lines", {
                                  count: order.days_late,
                                  lines: order.late_line_count,
                                })}
                          </Badge>
                        )}
                        {order.is_stale && (
                          <Badge variant="neutral" size="sm">
                            {t("replenishment.late.stale", "stale: close it")}
                          </Badge>
                        )}
                        {order.is_on_hold && (
                          <Badge variant={order.is_hold_alert ? "error" : "neutral"} size="sm">
                            {t("replenishment.late.held-for", "held {{count}} d", { count: order.days_on_hold ?? 0 })}
                          </Badge>
                        )}
                      </div>
                    </td>
                    <td className={cn("px-3 py-2.5 text-right tabular-nums", order.days_late > 0 ? "text-risk" : "text-ink-muted")}>
                      {order.due_date ?? ""}
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums text-ink">{formatCad(order.outstanding_amount_cad)}</td>
                    <td className="px-4 py-2.5 text-right">
                      {order.is_on_hold && canWrite && (
                        <Button
                          variant="secondary"
                          size="sm"
                          loading={release.isPending && release.variables === order.purchase_order_id}
                          onClick={() => release.mutate(order.purchase_order_id)}
                        >
                          <PlayCircle className="size-4" />
                          {t("po.release", "Release")}
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

